import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { MagnifyingGlass, Buildings, Copy, Users as UsersIcon, MapPin, PencilSimple, CheckCircle, Warning, QrCode, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  pharmacy: "Pharmacy", vet: "Veterinary", dental: "Dental", skin_care: "Skin Care",
  cosmetic_tattoos: "Cosmetic Tattoo", gp_clinic: "GP Clinic", specialist_clinic: "Specialist", other: "Other"
};

export default function AdminOrgsPage() {
  const [orgs, setOrgs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);

  const [editingOrg, setEditingOrg] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [allCats, setAllCats] = useState(false);
  const [allowed, setAllowed] = useState([]);
  const [billingEmail, setBillingEmail] = useState("");

  const [qrOrg, setQrOrg] = useState(null);
  const [showQr, setShowQr] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      const [orgsRes, catsRes] = await Promise.all([
        axios.get(`${API}/admin/orgs?${params}`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
      ]);
      setOrgs(orgsRes.data.organizations || []);
      setCategories(catsRes.data.categories || []);
    } catch { toast.error("Failed to load orgs"); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = orgs.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.name || "").toLowerCase().includes(s) || (o.code || "").toLowerCase().includes(s) || (o.applicant?.email || "").toLowerCase().includes(s);
  });

  const openEdit = (org) => {
    setEditingOrg(org);
    setAllCats(!!org.all_categories);
    setAllowed(org.allowed_categories || []);
    setBillingEmail(org.billing_email || "");
    setShowEdit(true);
  };

  const toggleCat = (key) => setAllowed(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const handleSave = async () => {
    if (!editingOrg) return;
    try {
      await axios.put(`${API}/admin/orgs/${editingOrg.id}`, {
        all_categories: allCats, allowed_categories: allCats ? [] : allowed
      }, { withCredentials: true });
      await axios.put(`${API}/admin/orgs/${editingOrg.id}/billing-email`, { billing_email: billingEmail }, { withCredentials: true });
      toast.success("Updated");
      setShowEdit(false);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const verifyAddress = async (orgId, addrId) => {
    try {
      await axios.post(`${API}/admin/orgs/${orgId}/addresses/${addrId}/verify`, {}, { withCredentials: true });
      toast.success("Address verified");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success("Code copied"); };

  const catLabel = (key) => categories.find(c => c.key === key)?.label || key;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-orgs-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
        <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Organizations</h1>
        <p className="text-sm text-zinc-500 mt-1">{filtered.length} clinic{filtered.length === 1 ? "" : "s"}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search by name, code, or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-none" data-testid="org-search-input" />
        </div>
        <div className="flex gap-1">
          {["active", "pending", "rejected", "all"].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border transition-colors ${statusFilter === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-950"}`}
              data-testid={`org-status-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="font-mono text-sm text-zinc-500 text-center py-12">LOADING...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-12">No organizations found</p>
      ) : (
        <div className="space-y-3" data-testid="orgs-list">
          {filtered.map(org => (
            <div key={org.id} className="border border-zinc-200 hover:border-zinc-300 transition-colors" data-testid={`org-row-${org.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
                <div className="md:col-span-5 p-4 border-b md:border-b-0 md:border-r border-zinc-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Buildings size={14} className="text-[#0055FF]" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{TYPE_LABEL[org.type] || org.type}</span>
                  </div>
                  <p className="font-[Chivo] font-bold text-zinc-950">{org.name}</p>
                  <p className="text-xs text-zinc-500 mt-1">{org.applicant?.name} — {org.applicant?.email}</p>
                  {org.code && (
                    <button onClick={() => copyCode(org.code)} className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 hover:bg-zinc-200 transition-colors font-mono text-xs" data-testid={`org-code-${org.id}`}>
                      {org.code} <Copy size={12} />
                    </button>
                  )}
                </div>

                <div className="md:col-span-3 p-4 border-b md:border-b-0 md:border-r border-zinc-200">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">STATUS</p>
                  <p className={`text-sm font-semibold mt-0.5 ${org.status === "active" ? "text-emerald-600" : org.status === "pending" ? "text-amber-600" : "text-red-600"}`}>
                    {org.status}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-2">CATEGORIES</p>
                  {org.all_categories ? (
                    <span className="inline-flex items-center gap-1 mt-0.5 text-emerald-700 text-xs"><CheckCircle size={12} weight="fill" /> All</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(org.allowed_categories || []).map(k => (
                        <span key={k} className="inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200">{catLabel(k)}</span>
                      ))}
                      {(!org.allowed_categories || org.allowed_categories.length === 0) && <span className="text-xs text-red-500">None</span>}
                    </div>
                  )}
                </div>

                <div className="md:col-span-3 p-4 border-b md:border-b-0 md:border-r border-zinc-200">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500"><UsersIcon size={10} className="inline" /> MEMBERS</p>
                  <p className="text-sm font-semibold text-zinc-950 mt-0.5">{org.member_count || 0}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-2"><MapPin size={10} className="inline" /> ADDRESSES</p>
                  <p className="text-sm font-semibold text-zinc-950 mt-0.5">
                    {(org.addresses || []).length}
                    {org.pending_addresses > 0 && <span className="ml-2 text-xs text-amber-600 font-mono">{org.pending_addresses} unverified</span>}
                  </p>
                </div>

                <div className="md:col-span-1 p-4 flex items-center justify-center gap-1">
                  {org.code && (
                    <button onClick={() => { setQrOrg(org); setShowQr(true); }} className="p-1.5 text-zinc-400 hover:text-[#0055FF]" title="Show QR" data-testid={`qr-org-${org.id}`}>
                      <QrCode size={18} />
                    </button>
                  )}
                  <button onClick={() => openEdit(org)} className="p-1.5 text-zinc-400 hover:text-[#0055FF]" data-testid={`edit-org-${org.id}`}>
                    <PencilSimple size={18} />
                  </button>
                </div>
              </div>

              {/* Expanded addresses if pending */}
              {org.pending_addresses > 0 && (
                <div className="border-t border-zinc-200 p-3 bg-amber-50/40">
                  <p className="font-mono text-xs uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1"><Warning size={12} /> Unverified Addresses</p>
                  {(org.addresses || []).filter(a => !a.verified).map(a => (
                    <div key={a.id} className="flex items-center justify-between py-1.5 text-xs" data-testid={`unverified-addr-${a.id}`}>
                      <span className="text-zinc-700">{a.street_line1}, {a.suburb} {a.state} {a.postcode}</span>
                      <button onClick={() => verifyAddress(org.id, a.id)} className="text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1" data-testid={`verify-addr-${a.id}`}>
                        <CheckCircle size={14} /> Verify
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit categories dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="sm:max-w-md" data-testid="edit-org-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Update Access</DialogTitle></DialogHeader>
          {editingOrg && (
            <div className="space-y-4">
              <div className="p-3 border border-zinc-200 bg-zinc-50">
                <p className="text-sm font-bold text-zinc-950">{editingOrg.name}</p>
                <p className="font-mono text-xs text-zinc-500 mt-0.5">{editingOrg.code || "—"}</p>
              </div>
              <div data-testid="billing-email-field">
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Billing Email</Label>
                <Input value={billingEmail} onChange={e => setBillingEmail(e.target.value)} placeholder="Defaults to org admin email" className="mt-1.5 rounded-none" data-testid="billing-email-input" />
                <p className="text-[10px] text-zinc-500 mt-1">Monthly statements (PDF + CSV) will be auto-sent here on the 1st of each month at 9am AEST.</p>
              </div>
              <label className="flex items-center gap-3 p-2 border border-zinc-200 cursor-pointer hover:bg-zinc-50">
                <Checkbox checked={allCats} onCheckedChange={c => setAllCats(!!c)} data-testid="edit-all-cats-toggle" />
                <p className="text-sm">All categories</p>
              </label>
              {!allCats && (
                <div>
                  <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Allowed Categories</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {categories.map(c => (
                      <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 border border-zinc-200 cursor-pointer hover:bg-zinc-50">
                        <Checkbox checked={allowed.includes(c.key)} onCheckedChange={() => toggleCat(c.key)} />
                        <span className="text-xs text-zinc-700">{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={handleSave} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC]" data-testid="save-org-edit">SAVE</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* QR Dialog */}
      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="sm:max-w-md" data-testid="qr-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Invite Team — {qrOrg?.name}</DialogTitle></DialogHeader>
          {qrOrg && (
            <div id={`qr-print-${qrOrg.id}`} className="space-y-4 print:p-8">
              <p className="text-sm text-zinc-600 print:text-base">Share this QR code or the org code with your team. They scan to land on a pre-filled join page.</p>
              <div className="flex flex-col items-center bg-white border border-zinc-200 p-6">
                <QRCodeSVG
                  value={`${window.location.origin}/login?join=${qrOrg.code}`}
                  size={220}
                  level="M"
                  marginSize={2}
                  data-testid="org-qr-code"
                />
                <p className="font-mono text-2xl font-bold text-zinc-950 mt-4 tracking-widest" data-testid="org-qr-code-text">{qrOrg.code}</p>
                <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">{qrOrg.name}</p>
              </div>
              <div className="flex gap-2 print:hidden">
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/login?join=${qrOrg.code}`); toast.success("Join link copied"); }} className="flex-1 px-4 py-2 text-sm bg-zinc-100 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2" data-testid="copy-join-link">
                  <Copy size={14} /> Copy Join Link
                </button>
                <button onClick={() => window.print()} className="flex-1 px-4 py-2 text-sm bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors flex items-center justify-center gap-2" data-testid="print-qr-button">
                  <Printer size={14} /> Print
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
